import React from 'react';
import { getTaskStatusBackgroundClass, getTaskStatusTextClass } from '../../utils/taskStatusUtils';
import ProgressBar from '../ui/ProgressBar';
import WorkerOnlineStatus from '../ui/WorkerOnlineStatus';

interface FieldVisibility {
  taskType: boolean;
  language: boolean;
  assignee: boolean;
  progress: boolean;
  deadline: boolean;
  price: boolean;
  taskId: boolean;
}

interface TaskCardProps {
  task: {
    id: string;
    type: string;
    sourceLanguage: string;
    targetLanguage: string | null;
    assignmentType: string;
    assignedToName: string;
    baseUnitPrice: number;
    priceAdjustment: number;
    finalUnitPrice: number;
    progress: string;
    deadline?: string;
    alertLevel?: 'warning' | 'error' | null;
    progressPercentage?: number;
    isWorkerOnline?: boolean;
    assignmentStartTime?: string;
  };
  showLanguage?: boolean;
  showTaskType?: boolean;
  compact?: boolean;
  fieldVisibility?: FieldVisibility;
  formatDeadline?: (deadline: string) => string;
}

const TaskCard: React.FC<TaskCardProps> = ({ 
  task, 
  showLanguage = true, 
  showTaskType = true,
  compact = false,
  fieldVisibility,
  formatDeadline
}) => {
  // fieldVisibility가 없으면 기본값 사용
  const visibility = fieldVisibility || {
    taskType: true,
    language: true,
    assignee: true,
    progress: true,
    deadline: true,
    price: true,
    taskId: false
  };
  // 배정 타입별 아이콘
  const getAssignmentIcon = (type: string) => {
    switch (type) {
      case 'worker': 
        return <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>;
      case 'group': 
        return <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>;
      case 'ai': 
        return <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>;
      case 'pm': 
        return <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>;
      case 'auto': 
        return <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>;
      default: 
        return <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>;
    }
  };

  // TaskType별 색상
  const getTaskTypeColor = (type: string) => {
    switch (type) {
      case '전사': return 'bg-violet-500 text-white';
      case '전사검수': return 'bg-violet-600 text-white';
      case '번역': return 'bg-blue-500 text-white';
      case '번역검수': return 'bg-blue-600 text-white';
      default: return 'bg-slate-500 text-white';
    }
  };

  // 상태 pill 스타일 (기본 progress 상태만 반영)
  const getStatusPillStyle = (progress: string) => {
    switch (progress) {
      case '완료': return 'bg-green-100 text-green-800';
      case '진행중': return 'bg-blue-100 text-blue-800';
      case '대기': return 'bg-gray-100 text-gray-700';
      case '취소': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className={`${getTaskStatusBackgroundClass(task.progress, task.alertLevel, task.deadline)} border hover:shadow-md transition-all duration-200 ${compact ? 'p-2 w-36 min-h-[80px]' : 'p-3 w-48 min-h-[100px]'}`}>
      {/* 상단: TaskType과 언어 정보 */}
      <div className="mb-1">
        {showTaskType && visibility.taskType && (
          <div className="mb-1">
            <span className={`px-1 py-0.5 text-xs font-medium ${getTaskTypeColor(task.type)}`}>
              {task.type}
            </span>
          </div>
        )}
        {showLanguage && visibility.language && (
          <div className="text-xs text-gray-600 font-bold truncate max-w-full overflow-hidden">
            <span className="block truncate">
              {task.sourceLanguage}
              {task.targetLanguage && ` → ${task.targetLanguage}`}
            </span>
          </div>
        )}
      </div>

      {/* 배정 정보 영역 */}
      {visibility.assignee && (
        <div className="mb-1 min-h-[20px] flex items-center">
          {task.assignmentType === 'worker' && task.progress !== '완료' ? (
            <WorkerOnlineStatus 
              isOnline={task.isWorkerOnline || false}
              workerName={task.assignedToName}
              size="sm"
              showName={true}
            />
          ) : task.assignmentType !== 'worker' && task.progress !== '완료' ? (
            <div className="flex items-center gap-1">
              <div className="text-gray-500">{getAssignmentIcon(task.assignmentType)}</div>
              <span className="text-xs text-gray-700 font-medium">
                배정중 ({(() => {
                  if (!task.assignmentStartTime) return '0m';
                  
                  try {
                    const now = new Date();
                    const startTime = new Date(task.assignmentStartTime);
                    const diffTime = now.getTime() - startTime.getTime();
                    const diffMinutes = Math.floor(diffTime / (1000 * 60));
                    const diffHours = Math.floor(diffMinutes / 60);
                    
                    if (diffHours > 0) {
                      return `${diffHours}h`;
                    } else {
                      return `${Math.max(diffMinutes, 1)}m`;
                    }
                  } catch {
                    return '0m';
                  }
                })()})
              </span>
            </div>
          ) : task.assignedToName ? (
            <div className="flex items-center gap-1">
              <div className="text-gray-500">{getAssignmentIcon(task.assignmentType)}</div>
              <span className="text-xs text-gray-700 font-medium truncate">{task.assignedToName}</span>
            </div>
          ) : (
            <div className="w-full min-h-[20px]"></div>
          )}
        </div>
      )}

      {/* 마감일 정보 영역 */}
      {visibility.deadline && (
        <div className="mb-1 min-h-[20px] flex items-center">
          {task.deadline ? (
            <div className="text-xs">
              {(() => {
                try {
                  const formattedDate = formatDeadline ? formatDeadline(task.deadline!) : new Date(task.deadline).toLocaleDateString('ko-KR', { 
                    month: 'numeric', 
                    day: 'numeric' 
                  });
                  
                  return (
                    <span className={getTaskStatusTextClass(task.progress, task.alertLevel, task.deadline)}>
                      {formattedDate}
                    </span>
                  );
                } catch {
                  return <span className="text-gray-600">{task.deadline}</span>;
                }
              })()}
            </div>
          ) : (
            <div className="w-full min-h-[20px]"></div>
          )}
        </div>
      )}

      {/* 상태 및 진척도 정보 영역 */}
      {visibility.progress && (
        <div className="mb-1 min-h-[20px] flex items-center justify-between">
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusPillStyle(task.progress)}`}>
            {task.progress}
          </div>
          {task.progress === '진행중' && task.progressPercentage !== undefined ? (
            <div className="flex-1 ml-2">
              <ProgressBar 
                progress={task.progressPercentage}
                size="sm"
                showPercentage={true}
              />
            </div>
          ) : (
            <div className="flex-1"></div>
          )}
        </div>
      )}

      {/* 금액 정보 영역 */}
      {visibility.price && (
        <div className="pt-1 min-h-[20px]">
          <div className="flex items-center justify-between">
            {task.finalUnitPrice ? (
              <div className="font-semibold text-gray-900 text-xs truncate">
                ₩{task.finalUnitPrice.toLocaleString()}
              </div>
            ) : (
              <div className="min-h-[16px]"></div>
            )}
            {task.priceAdjustment !== 0 ? (
              <div className={`text-xs font-medium ${task.priceAdjustment > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                {task.priceAdjustment > 0 ? '+' : ''}{task.priceAdjustment.toLocaleString()}
              </div>
            ) : (
              <div className="min-h-[16px]"></div>
            )}
          </div>
        </div>
      )}

      {/* Task ID 영역 */}
      {!compact && visibility.taskId && (
        <div className="mt-1 min-h-[16px]">
          {task.id ? (
            <div className="text-xs text-gray-400">
              {task.id}
            </div>
          ) : (
            <div className="w-full min-h-[16px]"></div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskCard;
